import tinycolor from "tinycolor2";
import { Components } from "@mui/material";
import { A2PTheme, ColorPalette } from "./theme";
const primary = "#90003B";
const secondary = "#36335F";
const bright = "#FAFAFF";
const warning = "#FF8F1F";
const success = "#6EB874";
const info = "#CACADB";
const NOTIFICATION_COLORS = {
  alert: "#DF0101",
  success: "#047205",
  info: "#006497",
  warning: "#A35E04",
  danger: "#DF0101",
};
export const UI: ColorPalette = {
  dark_blue_5: "#e6eeff",
  dark_blue_10: "#E6E6F0",
  dark_green: "#10BD10",
  black_300: "#B6B6B6",
  blue_20: "#CECEF5",
  blue_40: "#9999E0",
  blue_80: "#606060",
  blue_90: "#E1E1E1",
  blue_100: "#E8EEFF",
  blue_200: "#90C6E0",
  blue_1000: "#006497",
  purple_5: "#F8F2FF",
  purple_10: "#F7E7FF",
  purple_20: "#E3CCFF",
  purple_40: "#C799FF",
  purple_100: "#5C00CC",
  pink: "#EF0AA1",
  pink_80: "#CC0070",
  pink_100: "#FF008C",
  white: "#FFFFFF",
  black: "#000000",
  light_black: "#676767",
  grey_100: "#F7F7F7",
  grey_700: "#686868",
  green_1000: "#00B900",
  warn: "#705A00",
  grey_900: "#333333",
  grey_800: "#333333",
  grey_500: "#9C9C9C",
  grey_300: "#D1D1D1",
  grey_91: "grey_91",
  grey_98: "grey_98",
  secondary_rose: "#C53E6C",
  light_rose: "#F9EBF0",
  secondary_hover_rose: "#A53159",
  dark_rose: "#90003B",
  crimson_red: "#BF0000",
  red_50: "#FFE8E8",
  rose_200: "#FC7092",
  background: "#FFF0F0",
  grey: "#F2F2F2",
  text_grey: "#DADADA",
  text_grey_700: "#898989",
  red: "#E61717",
  // unselected_menu: "#686868",
  // placeholder_text: "#606060",
  orange: "#F59600",
  light_red: "#FF5959",
  navy: "#0019B9",
  disabled: "#DFDFDF",
  disabled_text: "#898989",
  grey_1000: "#F1F3F4",
  aqua: "#00B4E5",
  green: "#00B900",
  gold: "#F5E300",
  deep_red: "#8C0000",
};
const GRAPH = {
  blue: "#3B3BA3",
  orange: "#F59600",
  purple: "#C799FF",
  grey: "#9C9C9C",
  // QoS Dots, // including QoS panel
  success: "#10BD10",
  danger: "#DF0101",
  green: "#10BD10",
  red: "#E94D4D",
  defaultLinear: "#eaeaea",
  yellow: "#FFB238",
};
const lightenRate = 7.5;
const darkenRate = 15;
const theme: A2PTheme = {
  palette: {
    header: {
      default: primary,
    },
    background: {
      default: "#f2f5f7",
      bright: "#FFE6F4",
      light: "#E8EEFF",
      success: "#EBF7EC",
      danger: "#FFF0F0",
      error: "#FFF0F0",
      warn: "#FFF5CC",
      warning: "#FEF0DD",
      info: "#EBF7FE",
      alert: "#DF0101",
    },
    analytics: {
      volume: "#EBF7FE",
      revenue: "#EBF7EC",
      cost: "#FFF5CC",
      margin: "#FFF0F0",
    },
    text: {
      link: "#007ACC",
      link_focused: "#009BF5",
      link_active: "#146AA3",
      black: "#333333",
      primary: "#4A4A4A",
      secondary: "#6E6E6E",
      hint: "#B9B9B9",
      pageTitle: "#001e6d",
      success: "#2DE32D",
      warning: "#DF0101",
      danger: "#DF0101",
      info: "#000033",
      alert: "#990000",
      label: "#676767",
      text: "#DF0101",
      MediumGray: "#B3B3B3",
      placeholder_text: UI.grey_700,
      input: UI.grey_700,
    },
    NOTIFICATION_COLORS: NOTIFICATION_COLORS,
    UI: UI,
    GRAPH: GRAPH,
    primary: {
      main: primary,
      light: tinycolor(primary).lighten(lightenRate).toHexString(),
      dark: tinycolor(primary).darken(darkenRate).toHexString(),
    },
    secondary: {
      main: secondary,
      light: tinycolor(secondary).lighten(lightenRate).toHexString(),
      dark: tinycolor(secondary).darken(darkenRate).toHexString(),
      contrastText: "#FFFFFF",
    },
    bright: {
      main: bright,
      light: tinycolor(bright).lighten(lightenRate).toHexString(),
      dark: tinycolor(bright).darken(darkenRate).toHexString(),
    },
    warning: {
      main: warning,
      light: tinycolor(warning).lighten(lightenRate).toHexString(),
      dark: tinycolor(warning).darken(darkenRate).toHexString(),
    },
    success: {
      main: success,
      light: tinycolor(success).lighten(lightenRate).toHexString(),
      dark: tinycolor(success).darken(darkenRate).toHexString(),
    },
    info: {
      main: info,
      light: tinycolor(info).lighten(lightenRate).toHexString(),
      dark: tinycolor(info).darken(darkenRate).toHexString(),
    },
    purple: {
      main: UI.purple_40,
      light: tinycolor(UI.purple_40).lighten(lightenRate).toHexString(),
      dark: tinycolor(UI.purple_40).darken(darkenRate).toHexString(),
    },
    blue: {
      main: UI.blue_80,
      light: tinycolor(UI.blue_80).lighten(lightenRate).toHexString(),
      dark: tinycolor(UI.blue_80).darken(darkenRate).toHexString(),
    },
    pink: {
      main: UI.pink,
    },
  },
  button: {
    contained: {
      background: {
        primary: UI.secondary_rose,
        secondary: "",
        hover: UI.secondary_hover_rose,
        active: UI.dark_rose,
        enabled: UI.green_1000,
        suspended: UI.red,
        pending: UI.orange,
        retired: UI.navy,
        focus: UI.dark_rose,
        scheduled: UI.aqua,
        cancelled: UI.red,
        delivered: UI.green,
        undelivered: UI.aqua,
        expired: UI.gold,
        deleted: UI.grey,
        accepted: UI.green,
        enroute: UI.orange,
        unknown: UI.navy,
        rejected: UI.red,
        unsuccessful: UI.aqua,
        failed: UI.deep_red,
        dropped: UI.grey,
        partial: UI.green_1000
      },
      color: {
        primary: UI.white,
        secondary: UI.secondary_rose,
      },
    },
    outlined: {
      background: {
        primary: UI.white,
        secondary: "",
        hover: UI.secondary_hover_rose,
        disable: UI.grey_91,
      },
      color: {
        primary: UI.secondary_rose,
        secondary: "",
        hover: UI.white,
        disable: UI.grey_500,
      },
    },
  },
  dropdown: {
    text: UI.grey_900,
    selected: {
      color: UI.crimson_red,
      backgroundColor: UI.red_50,
    },
    unselected: {
      color: UI.blue_80,
      hover: {
        backgroundColor: UI.red_50,
        color: UI.grey_700,
      },
    },
  },
  chip: {
    text: UI.white,
    backgroundColor: UI.secondary_rose,
    active: {
      backgroundColor: UI.secondary_rose,
    },
  },
  input: {
    label: UI.blue_80,
  },
  switch: {
    backgroundColor: UI.grey_700,
    active: {
      backgroundColor: UI.dark_rose,
    },
  },
  calendar: {
    borderColor: UI.grey_500,
  },
  link: {
    color: UI.dark_rose,
    active: UI.secondary_rose,
    hover: UI.secondary_rose,
  },
  template: {
    borderColor: UI.grey_100,
    hover: UI.secondary_hover_rose,
    active: UI.secondary_rose,
    breadcrumbColor: UI.secondary_rose,
  },
  stepper: {
    active: UI.dark_rose,
    completed: UI.dark_rose,
  },
  tab: {
    color: UI.dark_rose,
    active: UI.secondary_rose,
  },
};

export const rcpMuiStyleOverrides: Components = {};
export default theme;
